import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, ImageOff, Upload, GripVertical } from "lucide-react";

const EMPTY = { name: "", description: "", image_url: null, order: 0, active: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef();

  const load = useCallback(() => api.get("/categories", { params: { all: true } }).then(({ data }) => setCategories(data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: categories.length }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "", image_url: c.image_url, order: c.order, active: c.active }); setOpen(true); };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, image_url: data.url })); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, form);
      else await api.post("/admin/categories", form);
      setOpen(false); load(); toast.success("Categoria salvata");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare la categoria?")) return; await api.delete(`/admin/categories/${id}`); load(); toast.success("Eliminata"); };
  const toggle = async (c) => { await api.put(`/admin/categories/${c.id}`, { ...c, active: !c.active }); load(); };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-slate-900">Gestione Categorie</h1>
        <Button onClick={openNew} className="rounded-full" data-testid="new-category-btn"><Plus className="w-4 h-4 mr-1" /> Nuova Categoria</Button>
      </div>
      <div className="mt-6 space-y-3">
        {categories.map((c) => (
          <Card key={c.id} className="p-4 rounded-2xl border-slate-200 flex items-center gap-4" data-testid={`admin-category-${c.id}`}>
            <GripVertical className="w-4 h-4 text-slate-300" />
            <div className="w-12 h-12 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">{fileUrl(c.image_url) ? <img src={fileUrl(c.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-5 h-5 text-emerald-200" />}</div>
            <div className="flex-1 min-w-0"><p className="font-semibold text-slate-900">{c.name}</p><p className="text-sm text-slate-500 truncate">{c.description}</p></div>
            <span className="text-xs text-slate-400">#{c.order}</span>
            <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`cat-active-${c.id}`} />
            <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-primary" data-testid={`cat-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(c.id)} className="p-1.5 text-slate-400 hover:text-rose-500" data-testid={`cat-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
        {!categories.length && <p className="text-sm text-slate-400">Nessuna categoria.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center">{fileUrl(form.image_url) ? <img src={fileUrl(form.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-5 h-5 text-emerald-200" />}</div>
              <input type="file" accept="image/*" ref={fileRef} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="hidden" data-testid="cat-image-input" />
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Immagine</Button>
            </div>
            <div><Label className="mb-1.5 block">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></div>
            <div><Label className="mb-1.5 block">Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-description" /></div>
            <div className="flex gap-4 items-center">
              <div className="flex-1"><Label className="mb-1.5 block">Ordine</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="cat-order" /></div>
              <label className="flex items-center gap-2 text-sm mt-6"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Attiva</label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="cat-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
